import { useSession } from 'components/auth/useSession'
import { useLembreteProfissionalByInputLazyQuery } from 'graphql/hooks.generated'
import { LembreteProfissionalIdentificadorEnum } from 'graphql/types.generated'
import {
  LembreteProfissionalSearchBy,
  useLembreteProfissional,
} from 'hooks/lembrete-profissional/useLembreteProfissional'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import { useTourContext } from '../components/TourContext'
import { TourStep } from '../types/model-tour'

/**
 * Tour manual: o id nao e persistido no backend e serve apenas como chave interna
 * do tour no TourContext. Use qualquer string unica por tela.
 */
export const useTour = (tourId: string, steps: TourStep[]) => {
  const { startTour } = useTourContext()

  const start = useCallback(
    (stepsOverride?: TourStep[]) => {
      const stepsToStart = stepsOverride ?? steps

      if (!stepsToStart.length) {
        return
      }

      startTour({ tourId, steps: stepsToStart })
    },
    [startTour, steps, tourId]
  )

  return { start }
}

interface UseAutoTourOptions {
  autoStart?: boolean
  searchBy?: LembreteProfissionalSearchBy
}

interface UseAutoTourResult {
  start: (stepsOverride?: TourStep[]) => void
  startPersisted: (stepsOverride?: TourStep[]) => boolean
  loading: boolean
  isAtivo: boolean
}

/**
 * Tour automatico + persistido: consulta o lembrete profissional associado ao
 * `identificador` e auto-inicia quando ainda nao foi visualizado. Ao terminar,
 * o tour marca o lembrete como visto, impedindo que reapareca. `start` tambem
 * e exposto para disparo manual (ex.: botao "Ver tour novamente"), independente
 * do estado do lembrete. `startPersisted` inicia em modo persistido, permitindo
 * informar passos especificos para o disparo.
 */
export const useAutoTour = (
  identificador: LembreteProfissionalIdentificadorEnum,
  steps: TourStep[],
  options: UseAutoTourOptions = {}
): UseAutoTourResult => {
  const { autoStart = true, searchBy = LembreteProfissionalSearchBy.USUARIO } = options
  const { startAutoTour, startTour } = useTourContext()
  const { data: session } = useSession({ fetchPolicy: 'cache-only' })
  const [loadLembreteProfissional, { data: lembreteProfissionalData }] = useLembreteProfissionalByInputLazyQuery({
    fetchPolicy: 'cache-only',
  })

  const { isAtivo, loading } = useLembreteProfissional({ identificador, searchBy })

  const lembreteInput = useMemo(() => {
    if (searchBy === LembreteProfissionalSearchBy.USUARIO) {
      const usuarioId = session?.profissional?.usuario?.id
      return usuarioId ? { usuarioId, identificador } : null
    }

    const atorPapelId = session?.acesso?.id
    return atorPapelId ? { atorPapelId, identificador } : null
  }, [identificador, searchBy, session])

  const lembreteId = lembreteProfissionalData?.lembreteProfissional?.id ?? null

  const shouldStart = autoStart && isAtivo && !!lembreteId
  const hasAutoStartedRef = useRef(false)

  const start = useCallback(
    (stepsOverride?: TourStep[]) => {
      const stepsToStart = stepsOverride ?? steps

      if (!stepsToStart.length) {
        return
      }

      startTour({ tourId: identificador, steps: stepsToStart })
    },
    [startTour, steps, identificador]
  )

  const startPersisted = useCallback(
    (stepsOverride?: TourStep[]) => {
      const stepsToStart = stepsOverride ?? steps

      if (!stepsToStart.length || !lembreteId) {
        return false
      }

      startAutoTour({ tourId: identificador, lembreteId, steps: stepsToStart })

      return true
    },
    [startAutoTour, steps, identificador, lembreteId]
  )

  useEffect(() => {
    hasAutoStartedRef.current = false
  }, [identificador])

  useEffect(() => {
    if (!lembreteInput) {
      return
    }

    void loadLembreteProfissional({
      variables: {
        input: lembreteInput,
      },
    })
  }, [lembreteInput, loadLembreteProfissional])

  useEffect(() => {
    if (!shouldStart) {
      hasAutoStartedRef.current = false
      return
    }

    if (hasAutoStartedRef.current) {
      return
    }

    if (!steps.length) {
      return
    }

    startPersisted()
    hasAutoStartedRef.current = true
  }, [shouldStart, startPersisted, steps])

  return { start, startPersisted, loading, isAtivo }
}
